import { uniq } from 'lodash';

import { AdvancementStatus } from '@appTypes/enums';
import type {
  CounselorMeritBadge,
  CounselorYouth,
  MeritBadge,
} from '@appTypes/esb';
import {
  filterMeritBadgeVersions,
  getLatestActiveVersion,
} from '@modules/bulkEntry/duck/utils/versions';

import { isActiveCounselorMB } from './selectors';
import type { MBVersion } from './types';
import { validateYouthEligibility } from './validations';

export interface Preselection {
  counselorMB: CounselorMeritBadge;
  mb: MeritBadge;
  selectedVersion?: MBVersion;
  selectedYouth: CounselorYouth[];
  skippedCount: number;
}

/**
 * Distinct versions the youth have started the MB in. More than one means a
 * single Bulk Entry version can't cover them all (see `otherVersion`)
 */
export function getStartedVersionIds(
  meritBadgeId: number,
  youth: CounselorYouth[],
): number[] {
  return uniq(
    youth.flatMap(item =>
      item.meritBadgesCounseling
        .filter(
          entry =>
            entry.meritBadgeId === meritBadgeId &&
            entry.status === AdvancementStatus.Started &&
            entry.versionId != null,
        )
        .map(entry => Number(entry.versionId)),
    ),
  );
}

/**
 * Version the youth already started when they all agree on one, so they aren't
 * all rejected as `otherVersion`. Otherwise the same default the page picks
 */
export function pickPreselectVersion(
  meritBadgeId: number,
  versions: MBVersion[],
  youth: CounselorYouth[],
): MBVersion | undefined {
  const startedVersionIds = getStartedVersionIds(meritBadgeId, youth);

  if (startedVersionIds.length === 1) {
    const offered = filterMeritBadgeVersions(versions) as MBVersion[];
    const started = offered.find(
      version => Number(version.versionId) === startedVersionIds[0],
    );
    if (started) return started;
  }

  return getLatestActiveVersion(versions);
}

/**
 * Resolves a MB + youth handed over from another page (e.g. a class) into the
 * Bulk Entry selection. `null` when the counselor can't enter that MB
 */
export function buildPreselection(params: {
  meritBadgeId: number;
  memberIds: number[];
  counselorMBs: CounselorMeritBadge[];
  meritBadge?: MeritBadge;
  youths: CounselorYouth[];
}): Preselection | null {
  const { meritBadgeId, memberIds, counselorMBs, meritBadge, youths } = params;

  const counselorMB = counselorMBs.find(
    item => item.meritBadgeId === meritBadgeId && isActiveCounselorMB(item),
  );
  if (!counselorMB || !meritBadge) return null;

  const requestedIds = new Set(memberIds);
  const requested = youths.filter(youth => requestedIds.has(youth.memberId));
  const selectedVersion = pickPreselectVersion(
    meritBadgeId,
    meritBadge.versions ?? [],
    requested,
  );
  const selectedYouth = requested.filter(
    youth =>
      validateYouthEligibility(youth, counselorMB, selectedVersion).valid,
  );

  return {
    counselorMB,
    mb: meritBadge,
    selectedVersion,
    selectedYouth,
    skippedCount: requestedIds.size - selectedYouth.length,
  };
}
